import { DATE_PIPE_DEFAULT_OPTIONS, DatePipeConfig } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  ErrorHandler,
  LOCALE_ID,
  Provider,
  isDevMode,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  withComponentInputBinding,
  withEnabledBlockingInitialNavigation,
} from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { BugsnagErrorHandler } from '@bugsnag/plugin-angular';
import { provideGlobalAuditsConfig } from '@kinet-open-network/client/audits/data-access';
import {
  AuthInterceptor,
  AuthResourceTablePresetAdapter,
  authEffects,
  authFeatureKey,
  authReducer,
  checkAbilityInterceptor,
  provideAuthConfig,
} from '@kinet-open-network/client/auth/data-access';
import {
  ALLOWED_DEALERS_SERVICE,
  ALLOWED_STOCKS_SERVICE,
  ALL_ACTIONS,
  CUSTOM_PERMISSIONS_SCHEMA,
  EXTENDED_CRUD_ACTIONS,
  PermissionSchemaGroup,
} from '@kinet-open-network/client/auth/feature/permissions-form';
import {
  API_URL,
  ENV_STAGE,
  apiUrlInterceptor,
} from '@kinet-open-network/client/core/api-client';
import {
  errorHandlerEffects,
  errorHandlerFeatureKey,
  errorHandlerReducer,
  errorHandlingInterceptor,
} from '@kinet-open-network/client/core/error-handler';
import {
  FIELDS_CUSTOMIZER,
  FormFieldsCustomizer,
  LIST_CUSTOMIZER,
  ListCustomizer,
  SELECTION_HANDLER,
  provideDynamicForms,
} from '@kinet-open-network/client/core/forms/ui';
import {
  routerFeatureKey,
  routerReducer,
} from '@kinet-open-network/client/core/router';
import {
  ProductPricesService,
  ProductPricesStore,
} from '@kinet-open-network/client/product-prices/data-access';
import { EXTENDED_MODELS_TOKEN } from '@kinet-open-network/client/shared/data-access/model';
import {
  RESOURCE_TABLE_PRESET_ADAPTER,
  RESOURCE_TABLE_STATE_KEY_PREFIX,
} from '@kinet-open-network/client/shared/ui';
import { StockMovesService } from '@kinet-open-network/client/stock-moves/data-access';
import { StocksService } from '@kinet-open-network/client/stocks/data-access';
import { UsersService } from '@kinet-open-network/client/users/data-access';
import {
  registerAbilitySubjects,
  registerAuditTypes,
  registerAutomationActions,
  registerAutomationContextTriggers,
  registerAutomationContexts,
  registerNotificationTypes,
  registerStockTypes,
} from '@kinet-open-network/shared/data-access/model';
import {
  customAuditTypeConfig,
  customSubjectMap,
  pbgasAbilitySubjects,
  pbgasAuditTypes,
  pbgasAutomationActionsData,
  pbgasAutomationContextTriggersData,
  pbgasAutomationContextsData,
  pbgasNotificationTypeOptions,
  pbgasNotificationTypes,
  pbgasStockTypeOptions,
  pbgasStockTypes,
} from '@kinet-open-network/shared/data-access/model-pbgas';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { environment } from '../environments/environment';
import { AppProductPricesStore } from './+state/app-product-prices.store';
import { appRoutes } from './app.routes';
import { Theme } from './app.theme';
import { provideAutomationsBuilderConfig } from './automations/automation-builder.config';
import { customerFieldsCustomizer } from './fields-config/customer-fields.config';
import { deliveryFieldsCustomizer } from './fields-config/delivery-fields.config';
import { paymentTypeFieldsCustomizer } from './fields-config/payment-type-fields.config';
import {
  pubStockItemFieldsCustomizer,
  stockItemAdrFieldsCustomizer,
  stockItemFieldsCustomizer,
} from './fields-config/stock-items-fields.config';
import { stockMoveFieldsCustomizer } from './fields-config/stock-move-fields.config';
import { provideChangePricesFlowConfig } from './flows/change-prices/data-access/change-prices-flow.config';
import { provideGoodsReceiptFlowConfig } from './flows/goods-receipts/data-access/goods-receipt-flow.config';
import { provideOrderFlowConfig } from './flows/orders/data-access/order-flow.config';
import { customerInvoicesListCustomization } from './list-config/customer-invoices-list.config';
import { customersListCustomization } from './list-config/customers-list.config';
import { deliveriesListCustomization } from './list-config/deliveries-list.config';
import { productPricesListCustomization } from './list-config/product-prices-list.config';
import { stockItemsListCustomization } from './list-config/stock-items-list.config';
import { AppCustomer } from './models/app-customer.model';
import { AppPaymentType } from './models/app-payment-type.model';
import { AppStockItem } from './models/app-stock-item.model';
import { AppSelectionHandler } from './selection/app-selection.handler';
import { AppProductPricesService } from './services/app-product-prices.service';
import { AppStockMovesService } from './services/app-stock-moves.service';

// Register pbgas-specific automation contexts, triggers, actions and ability subjects
registerAutomationContexts(pbgasAutomationContextsData);
registerAutomationContextTriggers(pbgasAutomationContextTriggersData);
registerAutomationActions(pbgasAutomationActionsData);
registerAbilitySubjects(pbgasAbilitySubjects, customSubjectMap);
registerAuditTypes(pbgasAuditTypes, customAuditTypeConfig);
registerStockTypes(pbgasStockTypes, pbgasStockTypeOptions);
registerNotificationTypes(pbgasNotificationTypes, pbgasNotificationTypeOptions);

function provideCustomSignalStores(): Provider[] {
  return [
    AppProductPricesStore,
    {
      provide: ProductPricesStore,
      useExisting: AppProductPricesStore,
    },
  ];
}

function provideCustomServices(): Provider[] {
  return [
    AppProductPricesService,
    {
      provide: ProductPricesService,
      useExisting: AppProductPricesService,
    },
    AppStockMovesService,
    {
      provide: StockMovesService,
      useExisting: AppStockMovesService,
    },
  ];
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      appRoutes,
      withEnabledBlockingInitialNavigation(),
      withComponentInputBinding(),
    ),
    provideStore({
      [authFeatureKey]: authReducer,
      [errorHandlerFeatureKey]: errorHandlerReducer,
      [routerFeatureKey]: routerReducer,
    }),
    provideEffects(authEffects, errorHandlerEffects),
    provideAuthConfig({
      show2faPrompt: !environment.disable_2fa_prompt,
    }),
    // provideRouterStore(),
    isDevMode() ? provideStoreDevtools() : [],
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([
        apiUrlInterceptor,
        errorHandlingInterceptor,
        checkAbilityInterceptor,
      ]),
    ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: API_URL,
      useValue: environment.api_url || 'http://localhost:3000/api/v2', // TODO: move to environment
    },
    {
      provide: LOCALE_ID,
      useValue: 'sk-SK',
    },
    {
      provide: DATE_PIPE_DEFAULT_OPTIONS,
      useValue: {
        dateFormat: 'd. MMM yyyy, HH:mm',
      } satisfies DatePipeConfig,
    },
    {
      provide: DEFAULT_CURRENCY_CODE,
      useValue: 'EUR',
    },
    {
      provide: RESOURCE_TABLE_STATE_KEY_PREFIX,
      useValue: 'kon_',
    },
    {
      provide: RESOURCE_TABLE_PRESET_ADAPTER,
      useExisting: AuthResourceTablePresetAdapter,
    },
    {
      provide: ENV_STAGE,
      useValue: environment.stage,
    },
    provideDynamicForms(),
    provideAnimations(),
    MessageService,
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    {
      provide: new ErrorHandler(),
      useFactory: () => new BugsnagErrorHandler(),
    },
    providePrimeNG({
      theme: {
        preset: Theme,
        options: {
          cssLayer: {
            name: 'primeng',
            order: 'tailwind-base, primeng, tailwind-utilities',
          },
          darkModeSelector: '.app-dark-mode',
        },
      },
    }),
    provideGlobalAuditsConfig({
      enableDelete: isDevMode(),
    }),
    {
      provide: FIELDS_CUSTOMIZER,
      useValue: new Map<string, FormFieldsCustomizer>([
        ['Customer', customerFieldsCustomizer],
        ['Delivery', deliveryFieldsCustomizer],
        ['StockItem', stockItemFieldsCustomizer],
        ['StockItemAdr', stockItemAdrFieldsCustomizer],
        ['PubStockItem', pubStockItemFieldsCustomizer],
        ['StockMove', stockMoveFieldsCustomizer],
        ['PaymentType', paymentTypeFieldsCustomizer],
      ]),
    },
    {
      provide: LIST_CUSTOMIZER,
      useValue: new Map<string, ListCustomizer>([
        ['Customer', customersListCustomization],
        ['StockItem', stockItemsListCustomization],
        ['ProductPrice', productPricesListCustomization],
        ['CustomerInvoices', customerInvoicesListCustomization],
        ['Delivery', deliveriesListCustomization],
      ]),
    },
    ...provideCustomSignalStores(),
    ...provideCustomServices(),
    AppSelectionHandler,
    { provide: SELECTION_HANDLER, useExisting: AppSelectionHandler },
    provideOrderFlowConfig(),
    provideChangePricesFlowConfig(),
    provideGoodsReceiptFlowConfig(),
    provideAutomationsBuilderConfig(),
    {
      provide: EXTENDED_MODELS_TOKEN,
      useValue: {
        StockItem: AppStockItem,
        Customer: AppCustomer,
        PaymentType: AppPaymentType,
      },
    },
    {
      provide: CUSTOM_PERMISSIONS_SCHEMA,
      useValue: [
        {
          title: 'Objednávky',
          subjects: [
            {
              subject: 'Order',
              title: 'Objednávka',
              actions: [...EXTENDED_CRUD_ACTIONS, 'options'],
            },
            {
              subject: 'ChangePrice',
              title: 'Cenová ponuka',
              actions: [...EXTENDED_CRUD_ACTIONS, 'options'],
            },
            {
              subject: 'GoodsReceipt',
              title: 'Príjem tovaru',
              actions: EXTENDED_CRUD_ACTIONS,
            },
          ],
        },
        {
          title: 'Zákazník',
          subjects: [
            {
              subject: 'Customer',
              title: 'Zákazník',
              actions: ALL_ACTIONS,
            },
          ],
        },
      ] satisfies PermissionSchemaGroup[],
    },
    {
      provide: ALLOWED_STOCKS_SERVICE,
      useValue: StocksService,
    },
    {
      provide: ALLOWED_DEALERS_SERVICE,
      useValue: UsersService,
    },
    provideTranslateService({
      fallbackLang: 'sk',
      lang: 'sk',
      loader: provideTranslateHttpLoader({
        prefix: '/assets/i18n/',
        suffix: '.json',
        useHttpBackend: true,
      }),
    }),
  ],
};
